import { useState, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { getImportClientDetail, importBulkClient, updateExisting } from "store/clients"
import { toaster } from "components/common/Toast"

/**
 * Custom hook to handle client import logic
 * Follows Single Responsibility Principle by isolating import business logic
 *
 * @param {Object} params - Hook parameters
 * @param {Function} params.onSuccess - Callback function on successful import
 * @param {Function} params.onError - Callback function on import error
 * @param {string|number} params.reuploadId - Optional ID for re-uploading existing import
 * @returns {Object} Import state and handlers
 */
const useClientImport = ({ onSuccess, onError, reuploadId, setOpenImportClientList }) => {
  const dispatch = useDispatch()
  const clientsState = useSelector((state) => state?.clients)
  const [fileData, setFileData] = useState(null)
  const isLoading = clientsState?.loading || clientsState?.importClientData?.loading

  /**
   * Handles file upload and import process
   * Memoized to prevent unnecessary re-creation
   */
  const handleImport = useCallback(async () => {
    if (!fileData) return

    const formData = new FormData()
    formData.append("file", fileData)

    if (reuploadId) {
      formData.append("id", reuploadId)
    }

    const action = reuploadId ? updateExisting(formData, reuploadId) : importBulkClient(formData)

    try {
      const result = await dispatch(action)

      if (result?.message) {
        toaster.success(result.message)
      }

      onSuccess?.(result)
      setOpenImportClientList(false)
    } catch (error) {
      onError?.(error)
    } finally {
      if (reuploadId) dispatch(getImportClientDetail(reuploadId))
    }
  }, [fileData, reuploadId, dispatch, onSuccess, onError])

  /**
   * Handles file selection
   * Memoized to prevent unnecessary re-creation
   */
  const handleFileChange = useCallback((file) => {
    setFileData(file)
  }, [])

  /**
   * Clears the selected file
   * Memoized to prevent unnecessary re-creation
   */
  const clearFile = useCallback(() => {
    setFileData(null)
  }, [])

  return {
    fileData,
    isLoading,
    handleFileChange,
    handleImport,
    clearFile
  }
}

export default useClientImport
